"use client";

import { c as _c } from "react/compiler-runtime";
import { ChangeEvent, FormEvent, Suspense, useState } from "react";
import { useRouter } from "next/navigation";
import { chakra, Input, InputGroup, Text } from "@chakra-ui/react";
import { IoSearchSharp } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import DynamicAddressAutofill, { AddressAutofillOptions, AddressAutofillRetrieveResponse } from "./address-autofill";
import { AddressAutofillSuggestionResponse } from "@mapbox/search-js-core";
const autofillOptions: AddressAutofillOptions = {
  country: "US",
  limit: 10,
  bbox: [-122.55, 37.69, -122.35, 37.83],
  proximity: {
    lng: -122.4194,
    lat: 37.7749
  },
  streets: false,
  language: "en"
};

// NOTE: UI changes to this page ought to be reflected in its suspense skeleton `search-bar-skeleton.tsx` and vice versa
// TODO: isolate the usage of `useSearchParams()` so that the Suspense boundary can be even more narrow if possible
interface SearchBarProps {
  inputAddress: string;
  onInputAddressChange: (address: string) => void;
  onSearchChange: (coords: number[], address: string) => void;
}
const SearchBar = t0 => {
  const $ = _c(37);
  const {
    inputAddress,
    onInputAddressChange,
    onSearchChange
  } = t0;
  const [suggestionSelected, setSuggestionSelected] = useState(false);
  const [suggestionsAvailable, setSuggestionsAvailable] = useState(false);
  const router = useRouter();
  let t1;
  if ($[0] !== onInputAddressChange || $[1] !== router) {
    t1 = () => {
      onInputAddressChange("");
      setSuggestionSelected(false);
      router.push("/", {
        scroll: false
      });
    };
    $[0] = onInputAddressChange;
    $[1] = router;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleClearClick = t1;
  let t2;
  if ($[3] !== onSearchChange) {
    t2 = res => {
      const addressData = res.features[0];
      const addressLine = res.features[0].properties.feature_name;
      const coords = addressData.geometry.coordinates;
      onSearchChange(coords, addressLine);
      setSuggestionSelected(true);
    };
    $[3] = onSearchChange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleRetrieve = t2;
  let t3;
  if ($[5] !== inputAddress.length || $[6] !== onInputAddressChange || $[7] !== suggestionSelected) {
    t3 = event => {
      onInputAddressChange(event.currentTarget.value);
      if (!suggestionSelected) {
        setSuggestionsAvailable(false);
      } else {
        if (suggestionSelected && inputAddress.length <= 3) {
          setSuggestionSelected(false);
          setSuggestionsAvailable(false);
        }
      }
    };
    $[5] = inputAddress.length;
    $[6] = onInputAddressChange;
    $[7] = suggestionSelected;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleAddressChange = t3;
  const onSubmit = _temp;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = res_0 => {
      setSuggestionsAvailable(res_0.suggestions.length > 0);
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleSuggest = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      base: "full",
      sm: "xs",
      md: "sm",
      lg: "md"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <IoSearchSharp color="grey.900" fontSize="1.1em" size="20" data-testid="search-icon" />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== handleClearClick || $[13] !== inputAddress.length) {
    t7 = inputAddress.length !== 0 && <RxCross2 color="grey.900" fontSize="1.1em" size="20" data-testid="clear-icon" onClick={handleClearClick} />;
    $[12] = handleClearClick;
    $[13] = inputAddress.length;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      base: "sm",
      sm: "md",
      md: "md",
      lg: "md"
    };
    t9 = {
      base: "lg",
      md: "xl",
      xl: "xl"
    };
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      base: "9",
      md: "12"
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      borderColor: "yellow"
    };
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      borderColor: "yellow",
      _placeholder: {
        color: "grey.900"
      }
    };
    t13 = {
      borderColor: "red"
    };
    $[19] = t12;
    $[20] = t13;
  } else {
    t12 = $[19];
    t13 = $[20];
  }
  let t14;
  if ($[21] !== handleAddressChange || $[22] !== inputAddress) {
    t14 = <Input placeholder="Search San Francisco address" fontFamily="body" fontSize={t8} size={t9} pt="0" pr="2.5" pb="0" pl={t10} borderRadius="full" border="search" bgColor="white" shadow="search" type="text" name="address-1" value={inputAddress} onChange={handleAddressChange} _focus={t11} _hover={t12} _invalid={t13} autoComplete="address-line1" />;
    $[21] = handleAddressChange;
    $[22] = inputAddress;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14 || $[25] !== t7) {
    t15 = <InputGroup w={t5} data-testid="search-bar" startElement={t6} endElement={t7}>{t14}</InputGroup>;
    $[24] = t14;
    $[25] = t7;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== handleRetrieve || $[28] !== t15) {
    t16 = <Suspense><DynamicAddressAutofill accessToken={process.env.NEXT_PUBLIC_MAPBOX_TOKEN ?? ""} options={autofillOptions} onRetrieve={handleRetrieve} onSuggest={handleSuggest}>{t15}</DynamicAddressAutofill></Suspense>;
    $[27] = handleRetrieve;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== inputAddress.length || $[31] !== suggestionSelected || $[32] !== suggestionsAvailable) {
    t17 = inputAddress.length && !suggestionSelected && !suggestionsAvailable ? <Text position="absolute" bottom="-5" textStyle="textSmall" color="white">{inputAddress.length < 5 ? "Keep typing\u2026" : "Try refining search\u2026"}</Text> : null;
    $[30] = inputAddress.length;
    $[31] = suggestionSelected;
    $[32] = suggestionsAvailable;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t16 || $[35] !== t17) {
    t18 = <chakra.form position="relative" onSubmit={onSubmit}>{t16}{t17}</chakra.form>;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  return t18;
};
export default SearchBar;
async function _temp(event_0) {
  console.log("onSubmit", event_0.currentTarget.value);
  event_0.preventDefault();
}